// 담당: 현정
// Creators — Performance 섹션. 워크스페이스 탭(muted 배경·패널 투명·흰색은 KPI 셀만).
//   KPI 3분할(평균 조회수·브랜디드 평균·인게이지먼트 L/S) · 브랜디드 추이(월별 L/S 2라인) ·
//   월별 매출 추이(막대+건수 라인) · Hub Program 바. 차트는 ../chart 공용 헬퍼(라이브러리 없음).
//   실 API만(fetchPerformance, mock 없음). null 지표 → "—"(0 표기 금지).
//   상태 B(전 KPI null) → 전역 배너 + KPI "—". 헤더 고지·배너·확정일은 전부 응답에서 런타임 파생
//   (coverage / meta{windowMonths, lastSyncedAt, confirmDays}). BE 미도달 시엔 현행 문구로
//   폴백해 화면이 비지 않는다(R9).
import { useState, type CSSProperties, type ReactNode } from 'react'
import { Info } from 'lucide-react'
import {
  fetchPerformance,
  setLecommendToken,
  type ContentFormat,
  type FormatPair,
  type MetricReason,
  type PerformanceData,
  type PerformanceSlice,
  type Platform,
  type ReasonPair,
} from '../creatorsApi'
import { InfoTip, TipRow, TipTitle } from './InfoTip'
import { PlatformTabs } from './PlatformTabs'
import { AiJudgedNotice } from './AiJudgedNotice'
import { ChartFrame, ChartTooltip, HoverBands, TrendRangeTabs, useTrendRange } from './trendRange'
import { HubProgramTracking } from './performance/HubProgramTracking'
import {
  MetricCaption,
  MetricNote,
  confirmDaysText,
  isMissing,
  resolveMetric,
  type MetricStatus,
} from './metric'
import { formatSlots, missingCaption, sampleText, valuePairText } from './platformFormat'
import { usePlatformData } from './usePlatformData'

import {
  buildBasisNote,
  formatCount,
  formatRate,
  formatYearMonth,
} from './overview/format'
import {
  lastDefined,
  lineSegments,
  maxDefined,
  pickAxisLabels,
  xBand,
  xPoint,
  yAt,
  type ChartGeom,
} from './chart'

interface Props {
  creatorId: string
  /** 셸이 URL 로 쥔 플랫폼 — 섹션을 옮겨도 유지된다. */
  platform: Platform
  /** 셸 계약(세 섹션 공통). 이 섹션은 보정을 하지 않아 소비하지 않는다 — 아래 load() 주석 참고. */
  platformPinned: boolean
  onPlatformChange: (platform: Platform) => void
}

/** 구버전 BE 폴백 — byPlatform 이 없으면 최상위 필드가 곧 그 채널(요청 채널)의 값이다. */
function sliceOf(d: PerformanceData): PerformanceSlice {
  const empty = { long: null, short: null }
  return {
    avgViews: d.avgViews,
    brandedAvgViews: d.brandedAvgViews,
    engagement: d.engagement,
    brandedViewTrend: d.brandedViewTrend,
    coverage: d.coverage ?? [],
    sampleSize: d.sampleSize ?? {},
    median: d.median ?? {},
    kpiReason: d.kpiReason ?? { avgViews: empty, brandedAvgViews: empty, engagement: empty },
    basis: d.basis ?? '',
    // 구버전 BE(byPlatform 미도달)에선 최상위가 곧 요청 채널의 값이다.
    meta: d.meta,
  }
}

export function PerformanceSection({ creatorId, platform: initialPlatform }: Props) {
  // 탭은 **블록마다 독립**이고 **표시 선택일 뿐**이다 — 세 채널을 미리 받아두므로 전환에 조회가 없다.
  // (예전엔 섹션이 platform 하나를 쥐고 탭마다 재조회해서, 한 블록을 바꾸면 다른 블록까지 끌려갔다.)
  const [kpiPlatform, setKpiPlatform] = useState<Platform>(initialPlatform)
  const [trendPlatform, setTrendPlatform] = useState<Platform>(initialPlatform)
  const [tokenInput, setTokenInput] = useState('')

  // 한 번 조회로 세 채널분이 온다 — 탭은 그중 무엇을 보여줄지만 고른다(전환 시 조회 없음).
  const perf = usePlatformData(creatorId, fetchPerformance, (d) => d.byPlatform)
  const shared = perf.data
  const error = perf.error
  // 구버전 BE(byPlatform 미도달)면 최상위 필드로 폴백한다 — 배포 순서와 무관하게 화면이 안 빈다.
  const fallback = shared ? sliceOf(shared) : undefined
  const kpi = perf.slice(kpiPlatform) ?? fallback
  const trend = perf.slice(trendPlatform) ?? fallback
  // ⚠️ 기본 플랫폼 보정은 여기서 하지 않는다. coverage 는 보유 채널을 전부 담고 오지만
  //    **요청한 플랫폼 행만 자기 소스로 채워지고** 나머지는 bi_base_data 기준이라 IG·TikTok 이 늘
  //    0으로 온다(같은 크리에이터가 YouTube 탭에선 "TikTok 수집 범위 밖", TikTok 탭에선 "TikTok
  //    기준"으로 뒤집히는 게 증거). 보정은 세 채널 표본이 정확한 Overview 가 맡고 URL 로 전파된다.

  function submitToken() {
    if (!tokenInput.trim()) return
    setLecommendToken(tokenInput.trim())
    setTokenInput('')
    perf.reload()
  }

  if (error) {
    const isAuth = error.status === 401
    return (
      <div className="border border-line-hairline bg-surface-base p-8">
        <div className="font-display text-[16px] font-bold text-fg-1">
          {isAuth ? '로그인이 필요합니다' : '데이터를 불러오지 못했습니다'}
        </div>
        <div className="mt-2 font-text text-[13px] text-fg-3">
          {isAuth
            ? 'Leferi Apps 로그인이 필요합니다.'
            : `${error.message}${error.transport && error.status ? ` (${error.status})` : ''}`}
        </div>
        {isAuth && import.meta.env.DEV && (
          <div className="mt-4 max-w-md">
            <div className="mb-2 font-text text-[12px] text-fg-3">
              로컬 dev — Bearer 토큰으로 실연결 테스트
            </div>
            <div className="flex gap-2">
              <input
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Bearer 토큰 붙여넣기"
                className="flex-1 rounded-r-1 border border-line-soft bg-surface-base px-2 py-1 text-fg-1 focus:shadow-focus focus:outline-none"
              />
              <button
                type="button"
                onClick={submitToken}
                className="rounded-r-1 bg-brand-blue px-3 py-1 font-medium text-fg-inverse hover:opacity-90 focus:shadow-focus focus:outline-none"
              >
                저장 후 재조회
              </button>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={perf.reload}
          className="mt-4 rounded-r-1 border border-line-soft px-3 py-1 font-text text-[13px] text-fg-1 hover:bg-surface-soft focus:shadow-focus focus:outline-none"
        >
          다시 시도
        </button>
      </div>
    )
  }

  if (!shared || !kpi) {
    return (
      <div className="border border-line-hairline bg-surface-base p-8 font-text text-[13px] text-fg-3">
        {perf.loading ? '불러오는 중…' : '표시할 데이터가 없습니다.'}
      </div>
    )
  }

  // 상태 B 판정 — coverage.supported에서 분리해 값 기준(allKpiNull)으로 일반화.
  //   YouTube 채널이 없는 크리에이터, IG만 있는 크리에이터도 같은 규칙으로 흡수된다.
  const stateB = [
    kpi.avgViews.long,
    kpi.avgViews.short,
    kpi.brandedAvgViews.long,
    kpi.brandedAvgViews.short,
    kpi.engagement.long,
    kpi.engagement.short,
  ].every((v) => v == null)

  // 배너 분기 — 채널 자체가 없는 경우(coverage=[])와 채널은 있으나 데이터가 없는 경우는 다른 사실이다.
  const noChannels = kpi.coverage.length === 0
  // meta 는 **그 탭의 조각**에서 온다. 최상위 meta 는 bi_base_data(YouTube 전용) 기준이라
  // 그대로 쓰면 확정일이 세 탭에서 똑같이 나온다 — 실제로는 플랫폼마다 다르다
  // (YouTube = engagement_days 실값 / Instagram = 5일 / TikTok = 2일).
  const kpiMeta = kpi.meta ?? shared.meta
  // "발행 N일"은 meta.confirmDays에서만 나온다(하드코딩 15일 금지). 미도달이면 일수 없이 표기.
  const confirmDays = confirmDaysText(kpiMeta?.confirmDays)
  // 요청한 탭이 아니라 BE 가 실제로 조회한 플랫폼으로 고지한다(KPI 셀과 같은 규칙).
  const coverageNote = buildCoverageNote(kpi.coverage, kpiMeta, kpi.basis, kpiPlatform)
  // 집계창 밖의 최신 발행월 — "창 안에 없음"과 "아예 없음"은 다른 사실이라 구분해 알린다.
  const lastMedia = lastMediaMonth(kpi.coverage)
  const kpiCtx: KpiContext = {
    // 요청한 탭이 아니라 BE 가 실제로 조회한 플랫폼을 쓴다 — 오타가 흡수됐을 때 라벨이 값과 어긋나지 않는다.
    platform: kpiPlatform,
    meta: kpiMeta,
    basis: kpi.basis,
    reason: uniformReason(kpi.coverage),
    lastMediaMonth: lastMedia,
  }

  return (
    <div>
      {/* 섹션 헤더 — 섹션 이름만 붙인다. 이 섹션의 블록이 전부 플랫폼별로 갈리는 게 아니라
          (매출·Hub Program 은 creatorId 만 받는다) 헤더에 탭을 두면 화면이 범위를 속인다. */}
      <div className="mb-3.5 flex items-center gap-2">
        <span className="inline-block h-[5px] w-[5px] flex-none bg-brand-blue" aria-hidden />
        <span className="font-text text-[11px] font-medium tracking-[0.16em] text-fg-3">
          PERFORMANCE · 성과 지표
        </span>
      </div>

      {/* 콘텐츠 지표 — 플랫폼 종속. 탭 · coverage 고지 · 상태 B 배너가 전부 이 블록 안에 산다.
          coverage 고지("TikTok 기준 …")도 플랫폼 종속 문구라 탭과 떨어지면 같은 거짓말이 된다. */}
      <div className="mb-2.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-text text-[14px] font-medium text-fg-1">콘텐츠 지표</span>
        <PlatformTabs
          value={kpiPlatform}
          onChange={setKpiPlatform}
          label="콘텐츠 지표 플랫폼"
          busy={perf.loading}
        />
        <span className="ml-auto inline-flex items-center gap-1.5 break-keep font-text text-[11px] text-fg-3">
          <Info className="h-3 w-3 flex-none" aria-hidden />
          {coverageNote}
        </span>
      </div>
      <AiJudgedNotice className="mb-3" />

      {/* 상태 B 전역 배너 — 정보(오류 아님)라 danger 색을 쓰지 않는다. 채널 부재 vs 데이터 부재 분기 */}
      {stateB && (
        <div className="mb-[22px] flex items-center gap-2.5 border border-line-soft border-l-[3px] border-l-brand-blue bg-surface-base p-[13px_16px]">
          <Info className="h-[15px] w-[15px] flex-none text-brand-blue" aria-hidden />
          <div className="break-keep">
            <span className="font-text text-[13px] font-medium text-fg-1">
              {noChannels ? '등록된 SNS 채널이 없습니다' : '집계 가능한 콘텐츠 데이터가 없습니다'}
            </span>
            <span className="ml-0.5 font-text text-[12px] text-fg-3">
              {' · '}
              {noChannels
                ? '어드민에서 채널을 등록하면 집계가 시작됩니다'
                : `${confirmDays ? `발행 ${confirmDays}일 ` : ''}확정 조회수가 쌓이면 자동으로 표시됩니다`}
              {!noChannels && lastMedia && ` · 마지막 집계 콘텐츠 ${lastMedia}`}
            </span>
          </div>
        </div>
      )}

      {/* KPI 3분할 — 값 옆 모수 1줄 + 셀 하단 산식 1줄(3셀 통일). 문구는 전부 응답에서 파생 */}
      <div className="grid grid-cols-1 gap-px border border-line-hairline bg-line-hairline sm:grid-cols-3">
        <KpiCell
          label="평균 조회수"
          formula="영상별 확정 조회수의 단순평균 (영상 단위 중복 제거)"
          note={`확정 조회수${confirmDays ? `(발행 ${confirmDays}일)` : ''} · 영상 단위 중복 제거`}
          values={kpi.avgViews}
          sampleSize={kpi.sampleSize.avgViews}
          reason={kpi.kpiReason.avgViews}
          median={kpi.median.avgViews}
          fmt={fmtViews}
          ctx={kpiCtx}
        />
        <KpiCell
          label="브랜디드 평균 조회수"
          formula="협찬 영상만 · 확정 조회수의 단순평균"
          note={`협찬 영상만 · 확정 조회수${confirmDays ? `(발행 ${confirmDays}일)` : ''}`}
          values={kpi.brandedAvgViews}
          sampleSize={kpi.sampleSize.brandedAvgViews}
          reason={kpi.kpiReason.brandedAvgViews}
          median={kpi.median.brandedAvgViews}
          fmt={fmtViews}
          ctx={kpiCtx}
        />
        <KpiCell
          label="인게이지먼트"
          formula="영상별 (좋아요+댓글)÷조회수의 평균 · 공유 미포함(소스 없음)"
          sampleNote="조회수 > 0 영상만"
          note="좋아요 + 댓글 ÷ 조회수"
          values={kpi.engagement}
          sampleSize={kpi.sampleSize.engagement}
          reason={kpi.kpiReason.engagement}
          fmt={fmtRate}
          ctx={kpiCtx}
        />
      </div>

      {/* 차트 2열 — 브랜디드 추이 | 월별 매출 */}
      <div className="mt-5 grid grid-cols-1 items-start gap-5 md:grid-cols-2">
        {/* 자기 탭·자기 채널 데이터 — 위 KPI 블록과 서로 간섭하지 않는다. */}
        <BrandedTrendChart
          trend={trend?.brandedViewTrend ?? []}
          stateB={!trend || trend.brandedViewTrend.length === 0}
          meta={trend?.meta ?? shared.meta}
          platform={trendPlatform}
          onPlatformChange={setTrendPlatform}
          busy={perf.loading}
        />
        {/* 매출은 Salesforce 라 채널 개념이 없다 — 어느 응답을 써도 같은 값이다. */}
        <BusinessTrendChart trend={shared.businessTrend} />
      </div>

      {/* Hub Program — portal 프록시가 enrich한 data.hubPrograms. 미연동/미매핑이면 빈 상태 graceful. */}
      {/* Hub Program 트래킹 — 소스가 cic 가 아니라 포털 Mongo 라 자체 로딩/빈 상태를 갖는다
          (Performance 응답의 deprecated `hubPrograms` 는 더 이상 쓰지 않는다). */}
      <HubProgramTracking creatorId={creatorId} />
    </div>
  )
}

const PLATFORM_KO: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
}

/** 화이트리스트 밖 social_type(SocialType.X 등)이 흘러와도 문구에 "undefined"가 찍히지 않게. */
function platformKo(platform: string): string {
  return PLATFORM_KO[platform] ?? (platform ? platform[0].toUpperCase() + platform.slice(1) : '기타')
}

// 헤더 우측 고지 — 조각 배열 → join(' · '). 빈 조각은 배열에서 빠질 뿐, else 분기로 대체 문구를
// 만들지 않는다(미수집 플랫폼이 없을 때도 "수집 예정"이 찍히던 원인).
//   · coverage undefined = 구버전 BE → 현행 문구 그대로 유지(R9 폴백. 배포 순서와 무관하게 안 빈다)
//   · coverage []        = 연결된 채널 없음 → 없는 집계창을 말하지 않는다
// 구버전 BE 폴백 — 집계창은 응답(meta)에서만 나온다. 여기에 기간을 적어두면 그 순간부터
//   화면이 데이터와 무관한 기간을 주장하게 된다(고정 기간 문구가 그렇게 남아 있었다).
/** BE `SocialMediaMetaProvider.SOURCE` 와 같은 값. IG/TikTok 은 확정 앵커가 없는 소스다. */
const SOCIAL_MEDIA_SOURCE = 'creator_social_media'
const LEGACY_COVERAGE_NOTE = 'YouTube 기준'
export function buildCoverageNote(
  coverage: PerformanceData['coverage'],
  meta: PerformanceData['meta'],
  basis: string | undefined,
  platform: Platform,
): string {
  if (!coverage) return LEGACY_COVERAGE_NOTE
  if (coverage.length === 0) return '연결된 채널 없음'
  // ⚠️ **이 응답이 실제로 어느 플랫폼 것인지만** 말한다. coverage 는 보유 채널을 전부 담고 오지만
  //    요청한 플랫폼 행만 자기 소스로 채워지고 나머지는 bi_base_data 기준이라 IG·TikTok 이 늘
  //    미지원으로 온다. 예전엔 그 목록을 그대로 join 해서, TikTok 에 영상이 154개인 크리에이터의
  //    YouTube 탭이 "TikTok 수집 범위 밖"이라고 단언했다. 못 믿는 값은 아예 말하지 않는다.
  const row = coverage.find((c) => c.platform === platform)
  const label = platformKo(platform)
  const parts: string[] = [row?.supported ? `${label} 기준` : `${label} 수집 범위 밖`]
  // 두 조각은 **다른 축**이다 — 앞은 어느 게시물을 담았나(대상 범위), 뒤는 그 게시물의 조회수를
  // 언제 시점으로 재나(값 정의). 예전엔 "전체 기간 · 최신 누적 조회수"처럼 나란히 붙어서,
  // 같은 축의 두 값으로 읽혀 "전체 기간인데 왜 최신이냐"는 모순으로 보였다. 축을 말로 가른다.
  parts.push(meta?.windowMonths ? `최근 ${meta.windowMonths}개월 발행` : '발행 전체 기간')
  // 탭마다 KPI 산식이 다르다 — YouTube 는 발행 확정 조회수, IG/TikTok 은 최신 누적값
  // (IG/TT 는 게시물당 추이 스냅샷이 1~2개뿐이라 "발행 N일 시점 값"을 복원할 수 없다).
  // 이 조각이 없으면 사용자가 탭을 오가며 서로 다른 정의의 숫자를 같은 것으로 비교한다.
  if (basis) parts.push(`값은 ${basis}`)
  return parts.join(' · ')
}

// 각 데이터 블록 제목 옆 ⓘ hover 툴팁 — 집계 기준(유저용 안내, 시스템명 미표기).
//   KPI 3분할의 ⓘ는 응답 파생 구조체(산식·집계창·모수·중앙값·확정일·수집)라 KpiCell 안에서 조립한다.
const TIP = {
  business: 'Financial(SF) 월별 매출·거래 건수 · 데이터 연동 시 표시',
  hub: '프로그램 소속 영상 누적 조회수의 롱/숏별 평균 · 증감은 최근 5회차 vs 직전 5회차',
} as const

/** 조회수 지표 — 만/억. null → "—". */
function fmtViews(v: number | null): string {
  return v == null ? '—' : formatCount(v)
}
/** 인게이지먼트 — 비율 → %. null → "—". */
function fmtRate(v: number | null): string {
  return v == null ? '—' : formatRate(v)
}

interface KpiContext {
  /** 어느 탭의 값인가. 포맷 두 칸의 어휘가 여기서 갈린다(FORMAT_LABELS). */
  platform: Platform
  /** 집계창·확정일(`meta.confirmDays`)의 단일 소스 — 확정일을 따로 들고 다니지 않는다. */
  meta: PerformanceData['meta']
  /** 값의 시점 정의("발행 확정 조회수" vs "최신 누적 조회수"). 집계창과 다른 축이라 따로 적는다. */
  basis?: string
  /** `kpiReason` 미도달(구버전 BE) 시에만 쓰는 폴백 — coverage 전 행이 같은 사유일 때 한정. */
  reason?: MetricReason
  /** 집계창 밖 최신 발행월("2025.03"). 없으면 null → 보조 문구 자체가 빠진다. */
  lastMediaMonth: string | null
}

/** coverage의 사유가 하나로 모아질 때만 그 사유를 쓴다. 섞여 있으면 추정하지 않는다(모호 → 미표기). */
function uniformReason(coverage: PerformanceData['coverage']): MetricReason | undefined {
  if (!coverage || coverage.length === 0) return undefined
  const first = coverage[0].reason
  return first && coverage.every((c) => c.reason === first) ? first : undefined
}

/** coverage[].lastMediaAt 중 최신 → "2025.03". 집계창과 무관한 값이라
 *  "최근 N개월 안에 없음"과 "콘텐츠가 아예 없음"을 가르는 유일한 근거다. */
function lastMediaMonth(coverage: PerformanceData['coverage']): string | null {
  const dates = (coverage ?? [])
    .map((c) => c.lastMediaAt)
    .filter((d): d is string => typeof d === 'string' && /^\d{4}-\d{2}/.test(d))
    .sort() // YYYY-MM-DD는 사전순 = 시간순
  const latest = dates[dates.length - 1]
  return latest ? formatYearMonth(latest.slice(0, 7)) : null
}

function KpiCell({
  label,
  formula,
  sampleNote,
  note,
  values,
  sampleSize,
  reason,
  median,
  fmt,
  ctx,
}: {
  label: string
  /** ⓘ 산식 — 지표 정의서(§7.1) 문장. */
  formula: string
  /** 모수 행 보조 설명(인게이지먼트는 모수 정의가 다르다). */
  sampleNote?: string
  /** 셀 하단 산식 1줄 — 3셀 모두 부여해 하단 baseline을 맞춘다. */
  note: string
  values: Record<ContentFormat, number | null>
  sampleSize?: FormatPair
  /** 지표별 롱/숏 결측 사유. 미도달이면 ctx.reason(coverage 폴백)으로 내려간다. */
  reason?: ReasonPair
  median?: FormatPair
  fmt: (v: number | null) => string
  ctx: KpiContext
}) {
  // kpiReason이 오면 그것만 신뢰한다(BE가 롱/숏을 분리 보장). 통째로 없을 때만 coverage 폴백.
  const reasons: ReasonPair = reason ?? { long: ctx.reason ?? null, short: ctx.reason ?? null }
  const long = resolveMetric(
    { value: values.long, sampleSize: sampleSize?.long, reason: reasons.long },
    { confirmDays: ctx.meta?.confirmDays },
  )
  const short = resolveMetric(
    { value: values.short, sampleSize: sampleSize?.short, reason: reasons.short },
    { confirmDays: ctx.meta?.confirmDays },
  )
  // 판정은 **그리는 칸만** 본다 — TikTok 은 영상 한 칸이라 long 슬롯의 결측은 화면에 없는 칸의 사정이다.
  const slots = formatSlots(ctx.platform)
  const drawn = slots.map((f) => (f.slot === 'long' ? long : short))
  // 값 옆 슬롯은 짧은 모수 표기 전용("표본 3건"). 결측 사유는 폭이 모자라 셀 1줄로 내린다.
  const missing = missingCaption(ctx.platform, { long, short })
  // 집계 콘텐츠가 0건인 결측에는 마지막 발행월을 덧붙인다 — 창 밖에 콘텐츠가 있는지가 다음 행동을 가른다.
  const showLastMedia =
    Boolean(missing) &&
    Boolean(ctx.lastMediaMonth) &&
    drawn.some((s) => s.state === 'no_content')
  const cellCaption = showLastMedia
    ? `${missing} · 마지막 집계 콘텐츠 ${ctx.lastMediaMonth}`
    : missing
  const showSampleSlot = drawn.some((s) => Boolean(s.caption))
  const samples = sampleText(ctx.platform, sampleSize)
  const confirm = confirmDaysText(ctx.meta?.confirmDays)
  // 확정 방식이 플랫폼마다 다르다. YouTube 는 engagement_completed=1 이라는 **확정 앵커**가 있어
  // "그 시점에 확정된 조회수"를 쓴다. IG/TikTok 엔 그 플래그가 없어 발행 경과일로 **거르기만** 하고,
  // 게시물당 추이 스냅샷이 1~2개뿐이라(YouTube 12.6) 발행 N일 시점 값을 복원할 수 없어 최신
  // 누적값을 쓴다. 둘을 같은 "확정일"로 적으면 IG 값이 그 시점에 확정된 것처럼 읽힌다.
  const snapshotOnly = ctx.meta?.source === SOCIAL_MEDIA_SOURCE
  const rows: [string, string][] = [
    ['산식', formula],
    ['집계창', ctx.meta?.windowMonths ? `최근 ${ctx.meta.windowMonths}개월 발행` : '발행 전체 기간'],
  ]
  if (samples) rows.push(['모수', sampleNote ? `${samples} (${sampleNote})` : samples])
  const medianText = valuePairText(ctx.platform, median, fmt)
  if (medianText) rows.push(['중앙값', medianText])
  if (confirm)
    rows.push(
      snapshotOnly
        ? ['집계 대상', `발행 ${confirm}일 지난 게시물`]
        : ['확정일', `발행 후 ${confirm}일`],
    )
  // 값의 시점 정의 — 집계창(대상 범위)과 다른 축이라 라벨로 갈라 적는다.
  if (ctx.basis) rows.push(['값 기준', ctx.basis])

  return (
    <div className="min-w-0 bg-surface-base p-[18px_20px]">
      <div className="flex items-center gap-1 font-text text-[12px] text-fg-3">
        {label}
        <InfoTip
          label={`${label} 집계 기준`}
          srText={`${label} — ${rows.map(([k, v]) => `${k} ${v}`).join(' · ')}`}
        >
          <TipTitle>{label}</TipTitle>
          {rows.map(([k, v]) => (
            <TipRow key={k} k={k} v={v} />
          ))}
        </InfoTip>
      </div>
      <div className="mt-3 flex gap-5">
        {slots.map((f) => (
          <ValuePair
            key={f.slot}
            ls={f.label}
            tone={f.slot}
            value={values[f.slot]}
            status={f.slot === 'long' ? long : short}
            fmt={fmt}
            showSampleSlot={showSampleSlot}
          />
        ))}
      </div>
      {cellCaption && (
        <div className="mt-1.5 min-h-4 break-keep">
          <MetricNote text={cellCaption} />
        </div>
      )}
      <div className="mt-2.5 border-t border-line-hairline pt-[9px] font-text text-[11px] leading-[1.4] text-fg-3">
        {note}
      </div>
    </div>
  )
}

/** 값 + 모수 슬롯. 결측 판정은 metric.tsx가 하고 여기선 렌더만 한다(문자열 '—' 역추론 제거). */
function ValuePair({
  ls,
  tone,
  value,
  status,
  fmt,
  showSampleSlot,
}: {
  ls: string
  tone: 'long' | 'short'
  value: number | null
  status: MetricStatus
  fmt: (v: number | null) => string
  showSampleSlot: boolean
}) {
  const na = !status.hasValue
  return (
    <div className="min-w-0">
      <div
        className={[
          'font-text text-[11px] tracking-[0.04em]',
          tone === 'long' ? 'text-fg-3' : 'text-brand-blue',
        ].join(' ')}
      >
        {ls}
      </div>
      <div
        className={[
          'mt-0.5 whitespace-nowrap font-display text-[24px] font-bold tabular-nums',
          na ? 'text-ink-400' : tone === 'long' ? 'text-ink-800' : 'text-brand-blue',
        ].join(' ')}
      >
        {na ? '—' : fmt(value)}
      </div>
      {/* 도트 유무·문구 길이와 무관하게 높이 고정 → 3셀 baseline 유지 */}
      {showSampleSlot && (
        <div className="mt-1.5 min-h-4">
          {!isMissing(status.state) && <MetricCaption status={status} />}
        </div>
      )}
    </div>
  )
}

function Panel({
  title,
  tip,
  scope,
  legend,
  actions,
  children,
}: {
  title: string
  tip?: string
  /** 제목 옆 **범위** 컨트롤(플랫폼 탭). 이 패널이 무엇에 대한 값인지를 바꾸는 자리다.
   *  기간 같은 **필터**(actions)와 역할이 달라 좌우로 갈라 둔다 — 한쪽에 몰면 위계가 무너진다. */
  scope?: ReactNode
  legend?: ReactNode
  /** 헤더 우측 컨트롤(기간 필터 등). 범례보다 바깥에 둔다. */
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="min-w-0 border border-line-hairline bg-paper p-[18px_20px]">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-2">
        <div className="flex items-center gap-2 font-text text-[14px] font-medium text-fg-1">
          <span className="flex items-center gap-1">
            {title}
            {tip && <InfoTip text={tip} />}
          </span>
          {scope}
        </div>
        <div className="flex flex-none items-center gap-3">
          {legend && (
            <div className="flex flex-none gap-3 font-text text-[11px] text-fg-3">{legend}</div>
          )}
          {actions}
        </div>
      </div>
      {children}
    </div>
  )
}

function LegendItem({
  color,
  dash,
  label,
  absent,
}: {
  color: string
  dash?: boolean
  label: string
  absent?: boolean
}) {
  // absent=시리즈 전체 null → dim + "(없음)"로 "데이터 없음"을 명시(범례까지 지우지 않음).
  return (
    <span
      className={['flex items-center gap-1.5 whitespace-nowrap', absent ? 'opacity-40' : ''].join(
        ' ',
      )}
    >
      <span
        className="inline-block"
        style={{ width: 10, height: dash ? 2 : 10, background: color }}
        aria-hidden
      />
      {label}
      {absent && ' (없음)'}
    </span>
  )
}

function EmptyBox({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mt-1 border border-dashed border-line-soft p-[18px_22px] text-center">
      <div className="font-text text-[13px] font-medium text-fg-2">{title}</div>
      <div className="mt-1.5 break-keep font-text text-[12px] leading-[1.55] text-fg-3">{sub}</div>
    </div>
  )
}

// 브랜디드 조회수 추이 — 월별 라인. 라인 수·범례 어휘는 플랫폼이 정한다(TikTok 은 영상 1라인).
const TREND_GEOM: ChartGeom = { w: 480, h: 170, padX: 20, top: 40, bottom: 140 }
// 라인 색은 **슬롯 기준**이라 어휘가 바뀌어도 유지된다(KPI 셀·채널 퍼포먼스와 같은 규칙).
const TREND_COLOR: Record<ContentFormat, string> = { long: 'var(--blue-600)', short: 'var(--blue-300)' }
function BrandedTrendChart({
  trend: all,
  stateB,
  meta,
  platform,
  onPlatformChange,
  busy,
}: {
  trend: PerformanceData['brandedViewTrend']
  stateB: boolean
  meta?: PerformanceData['meta']
  /** 그리는 라인과 범례 어휘가 여기서 갈린다 — TikTok 에 "Long (없음)"을 띄우지 않는다. */
  platform: Platform
  onPlatformChange: (platform: Platform) => void
  /** 새 플랫폼 응답 대기 중 — 탭 옆에 표시한다(아래 라인은 아직 이전 플랫폼 것이다). */
  busy?: boolean
}) {
  const slots = formatSlots(platform)
  // 값이 갈리는 패널이므로 자기 탭을 갖는다 — 옆의 매출 패널은 Salesforce 라 탭이 없고,
  // 그 유무가 곧 "어디까지가 이 플랫폼 이야기인가"를 화면에서 가른다.
  const scope = (
    <PlatformTabs value={platform} onChange={onPlatformChange} label="브랜디드 추이 플랫폼" busy={busy} />
  )
  // 기본 1년 — 전체 기간(수십 개월)은 '전체'로 언제든 볼 수 있다. 데이터는 버리지 않는다.
  const { range, setRange, rows: trend, options } = useTrendRange(all, 12)
  const [hover, setHover] = useState<number | null>(null)
  // 근거 3요소는 공용 빌더에서 뽑고(소스·집계창·수집일), 표시 구간과 결측월 해석을 덧붙인다.
  //   결측월은 "미수집"이 아닐 수 있다 — 신인 크리에이터는 전량 확정 대기다. 둘을 한 문구로 묶는다.
  //   전체 기간 전환으로 월 수가 수십 개가 되므로, 실제로 그린 구간을 밝혀 "일부만 그렸나"를 없앤다.
  const span =
    trend.length > 0
      ? `${formatYearMonth(trend[0].month)}–${formatYearMonth(trend[trend.length - 1].month)}`
      : null
  const trendTip = [
    buildBasisNote('협찬 영상의 발행월별 평균 조회수', meta),
    span ? `표시 구간 ${span}(${trend.length}개월 · 상단에서 기간 변경)` : null,
    '값 없는 달(미수집 또는 확정 대기)은 끊김으로 표시',
  ]
    .filter(Boolean)
    .join(' · ')
  const longVals = trend.map((t) => t.long)
  const shortVals = trend.map((t) => t.short)
  const valsOf = (slot: ContentFormat) => (slot === 'long' ? longVals : shortVals)
  const legend = (
    <>
      {slots.map((f) => (
        <LegendItem
          key={f.slot}
          color={TREND_COLOR[f.slot]}
          dash
          label={f.label}
          absent={trend.length > 0 && !valsOf(f.slot).some((v) => v != null)}
        />
      ))}
    </>
  )
  if (all.length === 0) {
    return (
      <Panel title="브랜디드 조회수 추이 · 월별" tip={trendTip} scope={scope} legend={legend}>
        <AiJudgedNotice className="mb-3" />
        <EmptyBox
          title={stateB ? '집계 가능한 콘텐츠 데이터가 없습니다' : '이 채널에는 브랜디드로 판정된 콘텐츠가 없습니다'}
          sub={
            stateB
              ? '확정 조회수가 쌓이면 이곳에 월별 브랜디드 조회수 추이가 표시됩니다.'
              : `협찬 콘텐츠가 쌓이면 이곳에 월별 ${slots.map((f) => f.label).join('·')} 추이가 표시됩니다.`
          }
        />
      </Panel>
    )
  }
  const n = trend.length
  const max = Math.max(maxDefined(longVals), maxDefined(shortVals), 0.0001) * 1.1
  // null 월은 세그먼트를 끊어 미표시(0으로 강제 금지). 시리즈 전체 null → 세그먼트 0개 → 라인 자체 미표시(범례는 dim).
  const shortSegs = lineSegments(shortVals, max, TREND_GEOM)
  const longSegs = lineSegments(longVals, max, TREND_GEOM)
  const shortLast = lastDefined(shortVals)
  const longLast = lastDefined(longVals)
  const hovered = hover != null ? trend[hover] : null
  return (
    <Panel
      title="브랜디드 조회수 추이 · 월별"
      tip={trendTip}
      scope={scope}
      legend={legend}
      actions={<TrendRangeTabs range={range} onChange={setRange} options={options} />}
    >
      <AiJudgedNotice className="mb-3" />
      <ChartFrame>
        {hovered && (
          <ChartTooltip
            xPct={((hover! + 0.5) / n) * 100}
            title={formatYearMonth(hovered.month)}
            rows={slots.map((f) => ({
              label: f.label,
              value: hovered[f.slot] == null ? '—' : formatCount(hovered[f.slot]),
              color: TREND_COLOR[f.slot],
            }))}
          />
        )}
      <svg viewBox={`0 0 ${TREND_GEOM.w} ${TREND_GEOM.h}`} className="block h-auto w-full">
        {hover != null && (
          <rect
            x={xPoint(hover, n, TREND_GEOM) - (TREND_GEOM.w - TREND_GEOM.padX * 2) / Math.max(n - 1, 1) / 2}
            y={20}
            width={(TREND_GEOM.w - TREND_GEOM.padX * 2) / Math.max(n - 1, 1)}
            height={130}
            fill="var(--bg-muted)"
            opacity={0.6}
          />
        )}
        {[40, 90, 140].map((y) => (
          <line
            key={y}
            x1={TREND_GEOM.padX}
            y1={y}
            x2={TREND_GEOM.w - TREND_GEOM.padX}
            y2={y}
            stroke="var(--ink-200)"
          />
        ))}
        {shortSegs.map((pts, i) => (
          <polyline
            key={`s${i}`}
            points={pts}
            fill="none"
            stroke="var(--blue-300)"
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {longSegs.map((pts, i) => (
          <polyline
            key={`l${i}`}
            points={pts}
            fill="none"
            stroke="var(--blue-600)"
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {shortLast >= 0 && (
          <circle
            cx={xPoint(shortLast, n, TREND_GEOM)}
            cy={yAt(shortVals[shortLast] as number, max, TREND_GEOM)}
            r={3.5}
            fill="var(--blue-300)"
          />
        )}
        {longLast >= 0 && (
          <circle
            cx={xPoint(longLast, n, TREND_GEOM)}
            cy={yAt(longVals[longLast] as number, max, TREND_GEOM)}
            r={3.5}
            fill="var(--blue-600)"
          />
        )}
        {hover != null && (
          <>
            {trend[hover].long != null && (
              <circle
                cx={xPoint(hover, n, TREND_GEOM)}
                cy={yAt(trend[hover].long as number, max, TREND_GEOM)}
                r={4}
                fill="var(--blue-600)"
              />
            )}
            {trend[hover].short != null && (
              <circle
                cx={xPoint(hover, n, TREND_GEOM)}
                cy={yAt(trend[hover].short as number, max, TREND_GEOM)}
                r={4}
                fill="var(--blue-300)"
              />
            )}
          </>
        )}
      </svg>
        <HoverBands count={n} onEnter={setHover} onLeave={() => setHover(null)} />
      </ChartFrame>
      {/* x축 — 점은 전부 그리고 라벨만 솎는다. 위치는 라인과 같은 xPoint에서 파생해 어긋나지 않는다 */}
      <div className="relative mt-2 h-[15px]">
        {pickAxisLabels(trend.map((t) => t.month)).map(({ i, text }) => (
          <span
            key={`${text}-${i}`}
            className="absolute top-0 whitespace-nowrap font-text text-[11px] text-fg-3"
            style={axisLabelStyle(i, n)}
          >
            {text}
          </span>
        ))}
      </div>
    </Panel>
  )
}

/** 양 끝 라벨은 컨테이너 밖으로 삐져나오지 않게 가장자리에 고정, 나머지는 점 중앙 정렬. */
function axisLabelStyle(i: number, n: number): CSSProperties {
  if (i === 0) return { left: 0 }
  if (i === n - 1) return { right: 0 }
  return { left: `${(xPoint(i, n, TREND_GEOM) / TREND_GEOM.w) * 100}%`, transform: 'translateX(-50%)' }
}

// 월별 비즈니스·매출 추이 — 매출 막대 + 건수 라인.
const BIZ_GEOM: ChartGeom = { w: 480, h: 160, padX: 24, top: 24, bottom: 140 }
function BusinessTrendChart({ trend: all }: { trend: PerformanceData['businessTrend'] }) {
  // 기본 1년 — 전 구간(수십 개월)을 한 화면에 밀어 넣으면 막대가 실처럼 얇아지고 축이 겹친다.
  const { range, setRange, rows: trend, options } = useTrendRange(all, 12)
  const [hover, setHover] = useState<number | null>(null)
  const revVals = trend.map((b) => b.revenue)
  const cntVals = trend.map((b) => b.count)
  const hasRev = revVals.some((v) => v != null)
  const hasCnt = cntVals.some((v) => v != null)
  const legend = (
    <>
      <LegendItem color="var(--blue-200)" label="매출(억)" absent={trend.length > 0 && !hasRev} />
      <LegendItem color="var(--blue-600)" dash label="건수" absent={trend.length > 0 && !hasCnt} />
    </>
  )
  if (all.length === 0) {
    return (
      <Panel title="월별 비즈니스 · 매출 추이" tip={TIP.business} legend={legend}>
        <EmptyBox
          title="매출 데이터가 아직 없습니다"
          sub="Financial 데이터가 연동되면 이곳에 월별 매출·거래 건수 추이가 표시됩니다."
        />
      </Panel>
    )
  }
  const n = trend.length
  const step = (BIZ_GEOM.w - BIZ_GEOM.padX * 2) / n
  const barW = Math.min(40, step * 0.5)
  const revMax = Math.max(maxDefined(revVals), 0.0001) * 1.1
  const cntMax = Math.max(maxDefined(cntVals), 0.0001)
  // null 월은 막대·라인 모두 미표시(0으로 강제 금지). 건수 라인은 null 갭에서 세그먼트를 끊는다.
  const cntSegs = lineSegments(cntVals, cntMax, BIZ_GEOM, xBand)
  const hovered = hover != null ? trend[hover] : null
  return (
    <Panel
      title="월별 비즈니스 · 매출 추이"
      tip={TIP.business}
      legend={legend}
      actions={<TrendRangeTabs range={range} onChange={setRange} options={options} />}
    >
      <ChartFrame>
        {hovered && (
          <ChartTooltip
            xPct={((hover! + 0.5) / n) * 100}
            title={formatYearMonth(hovered.month)}
            rows={[
              {
                label: '매출',
                value: hovered.revenue == null ? '—' : `${hovered.revenue.toFixed(2)}억`,
                color: 'var(--blue-200)',
              },
              {
                label: '거래 건수',
                value: hovered.count == null ? '—' : `${hovered.count}건`,
                color: 'var(--blue-600)',
              },
            ]}
          />
        )}
        <svg viewBox={`0 0 ${BIZ_GEOM.w} ${BIZ_GEOM.h}`} className="block h-auto w-full">
          <line
            x1={BIZ_GEOM.padX}
            y1={BIZ_GEOM.top}
            x2={BIZ_GEOM.w - BIZ_GEOM.padX}
            y2={BIZ_GEOM.top}
            stroke="var(--border-hairline)"
          />
          <line
            x1={BIZ_GEOM.padX}
            y1={(BIZ_GEOM.top + BIZ_GEOM.bottom) / 2}
            x2={BIZ_GEOM.w - BIZ_GEOM.padX}
            y2={(BIZ_GEOM.top + BIZ_GEOM.bottom) / 2}
            stroke="var(--border-hairline)"
          />
          <line
            x1={BIZ_GEOM.padX}
            y1={BIZ_GEOM.bottom}
            x2={BIZ_GEOM.w - BIZ_GEOM.padX}
            y2={BIZ_GEOM.bottom}
            stroke="var(--border-soft)"
          />
          {/* hover 중인 월 강조 밴드 — 값 위가 아니라 뒤에 깔아 데이터를 가리지 않는다 */}
          {hover != null && (
            <rect
              x={xBand(hover, n, BIZ_GEOM) - step / 2}
              y={BIZ_GEOM.top}
              width={step}
              height={BIZ_GEOM.bottom - BIZ_GEOM.top}
              fill="var(--bg-muted)"
              opacity={0.6}
            />
          )}
          {trend.map((b, i) => {
            if (b.revenue == null) return null // null 월 막대 미표시(0 막대 금지)
            const y = yAt(b.revenue, revMax, BIZ_GEOM)
            return (
              <rect
                key={b.month + i}
                x={xBand(i, n, BIZ_GEOM) - barW / 2}
                y={y}
                width={barW}
                height={BIZ_GEOM.bottom - y}
                fill={hover === i ? 'var(--blue-400)' : 'var(--blue-200)'}
              />
            )
          })}
          {cntSegs.map((pts, i) => (
            <polyline
              key={`c${i}`}
              points={pts}
              fill="none"
              stroke="var(--blue-600)"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          ))}
          {trend.map((b, i) =>
            b.count == null ? null : (
              <circle
                key={b.month + i}
                cx={xBand(i, n, BIZ_GEOM)}
                cy={yAt(b.count, cntMax, BIZ_GEOM)}
                r={hover === i ? 4 : 2.5}
                fill="var(--blue-600)"
              />
            ),
          )}
        </svg>
        <HoverBands count={n} onEnter={setHover} onLeave={() => setHover(null)} />
      </ChartFrame>
      {/* 축 라벨은 솎아낸다 — 전체 기간을 고르면 수십 개월이라 매월 표기는 겹쳐서 못 읽는다 */}
      <div className="relative mt-2 h-[14px]">
        {pickAxisLabels(trend.map((b) => b.month)).map(({ i, text }) => (
          <span
            key={i}
            className="absolute whitespace-nowrap font-text text-[11px] text-fg-3"
            style={axisLabelStyle(i, n)}
          >
            {text}
          </span>
        ))}
      </div>
    </Panel>
  )
}

